import { ApiErrorStatusCode } from 'lib-shared-auth';
import { Sentry, SentryTeam } from 'lib-shared-sentry';
import { useCallback, useRef } from 'react';

import { aiAgentsApi } from 'apis/aiAgents';

import { type MimUIMessage } from '../utils/sdk/structuredResponse';
import { generateConversationTitle } from '../utils/sdk/title';
import { toSidekickMessageDraft } from '../utils/sidekickMessages';

const SDK_VERSION = '7';

/** Named per turn, so switching conversations mid-stream cannot redirect it. */
type PersistTurnArg = {
  chatId: string;
  messages: MimUIMessage[];
};

type UseSidekickPersistenceResult = {
  persistTurn: (arg: PersistTurnArg) => Promise<void>;
  markExisting: (conversationId: string) => void;
};

const isConflict = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  'status' in error &&
  error.status === ApiErrorStatusCode.Conflict;

export const useSidekickPersistence = (): UseSidekickPersistenceResult => {
  const [createChat] =
    aiAgentsApi.useCreateAiAgentsSidekickConversationMutation();
  const [appendMessages] =
    aiAgentsApi.useCreateAiAgentsSidekickConversationMessagesMutation();

  const createdRef = useRef(new Map<string, Promise<void>>());

  const markExisting = useCallback((id: string) => {
    createdRef.current.set(id, Promise.resolve());
  }, []);

  const ensureConversation = useCallback(
    (
      conversationId: string,
      getTitle: () => Promise<string | undefined>,
    ): Promise<void> => {
      const existing = createdRef.current.get(conversationId);
      if (existing) return existing;

      const done = getTitle()
        .then((title) =>
          createChat({
            id: conversationId,
            title,
            sdkVersion: SDK_VERSION,
          }).unwrap(),
        )
        .then(() => undefined)
        .catch((error: unknown) => {
          if (isConflict(error)) return;
          createdRef.current.delete(conversationId);
          throw error;
        });

      createdRef.current.set(conversationId, done);
      return done;
    },
    [createChat],
  );

  const persistTurn = useCallback(
    async ({ chatId, messages }: PersistTurnArg) => {
      const stored = messages.map(toSidekickMessageDraft);
      if (stored.length === 0) return;

      try {
        await ensureConversation(chatId, () =>
          generateConversationTitle(messages),
        );
        await appendMessages({
          conversationId: chatId,
          sdkVersion: SDK_VERSION,
          messages: stored,
        }).unwrap();
      } catch (exception) {
        Sentry.captureException(
          new Error('Mim Sidekick: chat turn was not saved'),
          {
            tags: { team: SentryTeam.Automation },
            extra: {
              chatId,
              messageIds: stored.map((message) => message.messageId).join(', '),
              exception,
            },
          },
        );
      }
    },
    [appendMessages, ensureConversation],
  );

  return { persistTurn, markExisting };
};
